import { useEffect, useMemo, useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import InputAdornment from '@mui/material/InputAdornment'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import ICasino from '../../models/ICasino'
import { useAppState } from '../../reduxish/appState'
import { getCurrentPosition, haversineMeters, GeolocationError } from '../../api/geolocation'
import { reportMinBets, ReportResult } from '../../api/minBetsApi'
import { DEFAULT_CLIENT_CONFIG } from '../../api/configApi'

const GAME_TYPES: { key: 'craps' | 'blackjack' | 'roulette', label: string, has: (c: ICasino) => boolean }[] = [
    { key: 'craps', label: 'Craps', has: (c) => c.hasCraps },
    { key: 'blackjack', label: 'Blackjack', has: (c) => c.hasBlackjack },
    { key: 'roulette', label: 'Roulette', has: (c) => c.hasRoulette },
]

const REASON_MESSAGES: Record<string, string> = {
    too_far: 'You must be at the casino to report prices.',
    duplicate_recent: "Thanks — we already have that value from you. We've recorded the duplicate but it doesn't earn points.",
    inconsistent_recent: "You just reported a different value for this game. Please wait a few minutes before changing your report.",
    rate_limited: "You're submitting too fast. Please slow down.",
    poor_history: "Thanks — we've recorded your report. It'll count once your recent reports trend positive again.",
    value_outlier: 'That value looks way off from the current minimum. Double-check and try again.',
    game_not_offered: 'This casino does not offer that game.',
}

type Props = {
    open: boolean,
    onClose: () => void,
    casino: ICasino,
    onAcceptedBet?: (result: ReportResult) => void,
}

export default function ReportMinBetsDialog({ open, onClose, casino, onAcceptedBet }: Props) {
    const { state, dispatch } = useAppState()
    const role = state.auth?.authUser?.role ?? 'user'
    const isTrusted = role === 'admin' || role === 'moderator'
    const proximityMeters = state.clientConfig?.minBetProximityMeters ?? DEFAULT_CLIENT_CONFIG.minBetProximityMeters

    const [geoLoading, setGeoLoading] = useState(true)
    const [geoError, setGeoError] = useState<GeolocationError | null>(null)
    const [coords, setCoords] = useState<{ latitude: number, longitude: number } | null>(null)

    const [values, setValues] = useState<Record<string, string>>({})
    const [submitting, setSubmitting] = useState(false)
    const [rejectionMessage, setRejectionMessage] = useState<string | null>(null)
    const [submitError, setSubmitError] = useState('')

    // Reset when the dialog opens.
    useEffect(() => {
        if (!open) return
        setGeoLoading(true)
        setGeoError(null)
        setCoords(null)
        setValues({})
        setRejectionMessage(null)
        setSubmitError('')

        let cancelled = false
        getCurrentPosition().then((res) => {
            if (cancelled) return
            if (res.coords) setCoords(res.coords)
            else setGeoError(res.error ?? 'unavailable')
            setGeoLoading(false)
        })
        return () => { cancelled = true }
    }, [open])

    const distanceM = useMemo(() => {
        if (!coords || casino.latitude == null || casino.longitude == null) return null
        return haversineMeters(coords, { latitude: casino.latitude, longitude: casino.longitude })
    }, [coords, casino.latitude, casino.longitude])

    const tooFar = !isTrusted && distanceM != null && distanceM > proximityMeters
    const blockedByLocation = !isTrusted && (geoLoading || geoError !== null || tooFar)

    const availableGames = GAME_TYPES.filter((g) => g.has(casino))

    const parsedReports = availableGames
        .map((g) => ({ gameType: g.key, raw: (values[g.key] ?? '').trim() }))
        .filter((b) => b.raw !== '')
        .map((b) => ({ gameType: b.gameType, minBet: Number(b.raw) }))

    const hasInvalid = parsedReports.some((b) => !Number.isFinite(b.minBet) || b.minBet <= 0 || b.minBet >= 10000)
    const canSubmit = !blockedByLocation && !submitting && parsedReports.length > 0 && !hasInvalid

    const handleSubmit = async () => {
        if (!canSubmit) return
        setSubmitting(true)
        setRejectionMessage(null)
        setSubmitError('')
        try {
            const resp = await reportMinBets(casino.id, {
                latitude: coords?.latitude ?? 0,
                longitude: coords?.longitude ?? 0,
                reports: parsedReports,
            })
            dispatch({
                type: 'USER_POINTS_SET',
                points: { total: resp.total, monthly: resp.monthly, reportCount: resp.reportCount },
            })
            if (onAcceptedBet) {
                for (const r of resp.results) if (r.outcome === 'accepted') onAcceptedBet(r)
            }
            // 'replayed' is an idempotent retry — treat as user-visible success.
            // 'shadow_accepted' means the value was recorded but won't be public
            // yet; we close silently and let the user trend back up.
            const silentSuccess = (o: string) => o === 'accepted' || o === 'shadow_accepted'
            const allOk = resp.results.every((r) => silentSuccess(r.outcome))
            if (allOk) {
                onClose()
            } else {
                const firstRejection = resp.results.find((r) => !silentSuccess(r.outcome))
                const reason = firstRejection?.reasons[0] ?? 'rejected'
                setRejectionMessage(REASON_MESSAGES[reason] ?? "We couldn't count this report. Please try again.")
            }
        } catch (e: any) {
            setSubmitError(e?.message ?? 'Submission failed')
        } finally {
            setSubmitting(false)
        }
    }

    const locationMessage = () => {
        if (isTrusted) return null
        if (geoLoading) return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">Requesting location…</Typography>
            </Box>
        )
        if (geoError === 'denied') return <Alert severity="warning">Location access is required to report prices.</Alert>
        if (geoError) return <Alert severity="warning">Could not determine your location. Please try again.</Alert>
        if (tooFar) return <Alert severity="warning">You must be near {casino.shortName ?? casino.name} to report prices.</Alert>
        return null
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle>Report prices at {casino.shortName ?? casino.name}</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    {locationMessage()}

                    <Typography variant="body2" color="text.secondary">
                        Enter the current minimum bet for any games you've seen. Leave blank to skip.
                    </Typography>

                    {availableGames.map((g) => (
                        <TextField
                            key={g.key}
                            label={g.label}
                            value={values[g.key] ?? ''}
                            onChange={(e) => setValues((v) => ({ ...v, [g.key]: e.target.value }))}
                            disabled={blockedByLocation || submitting}
                            type="number"
                            placeholder="e.g. 15"
                            slotProps={{
                                htmlInput: { min: 1, max: 9999, step: 1, inputMode: 'decimal' },
                                input: { startAdornment: <InputAdornment position="start">$</InputAdornment> },
                            }}
                            fullWidth
                        />
                    ))}

                    {hasInvalid && (
                        <Alert severity="error">Values must be between $0 and $10,000.</Alert>
                    )}

                    {rejectionMessage && <Alert severity="warning">{rejectionMessage}</Alert>}

                    {submitError && <Alert severity="error">{submitError}</Alert>}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={submitting}>Close</Button>
                <Button onClick={handleSubmit} variant="contained" disabled={!canSubmit}>
                    {submitting ? <CircularProgress size={20} /> : 'Submit'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
